import { HttpClient, HttpHeaders, HttpResourceOptions } from '@angular/common/http';
import { effect, inject, resource, Signal, signal } from '@angular/core';

export function debounced<T>(
  source: Signal<T>,
  timeout: number,
): Signal<T> {
  const debounced = signal<T>(source());

  effect((onCleanup) => {
    const value = source();
    const t = setTimeout(() => debounced.set(value), timeout);

    onCleanup(() => clearTimeout(t));
  });

  return debounced;
}

/**
 * Like httpResource, but debounces the request.
 */
export function searchResource<T>(url: () => string | null, debounce: number, options?: HttpResourceOptions<T, unknown>) {
  const http = inject(HttpClient);
  return resource({
    params: () => ({ url: url() }),
    loader: async (p) => {
      if (p.params.url === null || p.params.url === undefined) {
        return;
      }

      let timeout: number;
      return await new Promise<T>((resolve, reject) => {
        p.abortSignal.addEventListener('abort', () => {
          window.clearTimeout(timeout);
          reject();
        }, { once: true });

        timeout = window.setTimeout(() => {
          http.get<T>(p.params.url!, {
            headers: new HttpHeaders({
              'Content-Type': 'application/json',
            }),
          }).subscribe({
            next: (result: unknown) => {
              if (options?.parse) {
                result = options.parse(result);
              }

              resolve(result as T);
            },
            error: (err) => {
              reject(err);
            }
          });
        }, debounce);
      });
    },
  });
}

